'use client';
import { FC, useEffect, useState } from 'react';
import { PreferencesState } from './types';
import CommunicationPreferenceCard from './CommunicationPreferenceCard';
import { usePageData } from '@/context/PageDataProvider';
import { TypeEditCommunicationPreferencesPageData } from '@/pages/[locale]/account/settings/edit-communication-preferences';
import { TypeEditCommunicationPreferencesFields } from '@/core/generated-types/TypeEditCommunicationPreferences';
import { TypeCommunicationPreference } from '@/core/generated-types/TypeCommunicationPreference';
import { SMS_PREFERENCES } from './constants';

const EditCommunicationPreferences: FC<
  TypeEditCommunicationPreferencesFields
> = ({
  communicationPreferences,
  marketingLabel,
  transactionalLabel,
  phoneNumberLabel,
  invalidPhoneNumberMessage,
  ctaText,
}) => {
  const pageData = usePageData() as TypeEditCommunicationPreferencesPageData;
  const { user } = pageData;
  const { user_preferences: userPreferences, uuid, phone_number } = user;
  const [phoneNumber, setPhoneNumber] = useState<string>(phone_number || '');
  const [previousPhoneNumber, setPreviousPhoneNumber] = useState<string>(
    phone_number || '',
  );

  const defaultPreferences: PreferencesState = communicationPreferences.reduce(
    (acc: PreferencesState, pref: TypeCommunicationPreference) => {
      pref.fields.preferenceTypes.forEach((type: string) => {
        acc[type] = { value: false };
      });
      return acc;
    },
    {} as PreferencesState,
  );

  const [preferences, setPreferences] =
    useState<PreferencesState>(defaultPreferences);
  const [savedPreferences, setSavedPreferences] = useState<PreferencesState>(
    {},
  );
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    const fetchedPreferences = userPreferences.reduce(
      (acc: PreferencesState, pref) => {
        if (SMS_PREFERENCES.includes(pref.preference_name)) {
          acc[pref.preference_name] = {
            value: pref.preference_value,
            uuid: pref.uuid,
          };
        }
        return acc;
      },
      {} as PreferencesState,
    );

    setPreferences((prev) => ({ ...prev, ...fetchedPreferences }));
    setSavedPreferences((prev) => ({ ...prev, ...fetchedPreferences }));
  }, [userPreferences]);

  useEffect(() => {
    const hasChanges = Object.keys(preferences).some(
      (key) => preferences[key]?.value !== savedPreferences[key]?.value,
    );
    const numberChanged = previousPhoneNumber !== phoneNumber;
    setIsChanged(hasChanges || numberChanged);
  }, [preferences, savedPreferences, previousPhoneNumber, phoneNumber]);

  const handleCheckboxChange = (key: string) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: { ...prev[key], value: !prev[key].value },
    }));
  };

  const updateSavedPreferences = () => {
    setSavedPreferences(preferences);
    setPreviousPhoneNumber(phoneNumber);
  };

  return (
    <div className="mb-20">
      {communicationPreferences.map(
        (communicationPreference: TypeCommunicationPreference) => (
          <CommunicationPreferenceCard
            key={communicationPreference.sys.id}
            userUuid={uuid}
            preferences={preferences}
            userPhoneNumber={phoneNumber}
            updateSavedPreferences={updateSavedPreferences}
            isChanged={isChanged}
            onCheckboxChange={handleCheckboxChange}
            onPhoneNumberChange={setPhoneNumber}
            marketingLabel={marketingLabel}
            transactionalLabel={transactionalLabel}
            phoneNumberLabel={phoneNumberLabel}
            invalidPhoneNumberMessage={invalidPhoneNumberMessage}
            ctaText={ctaText}
            {...communicationPreference.fields}
          />
        ),
      )}
    </div>
  );
};

export default EditCommunicationPreferences;
